import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  TextField,
  Typography,
  Divider,
  Paper,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Close from "@mui/icons-material/Close";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Check from "@mui/icons-material/Check";
import CreditCard from "@mui/icons-material/CreditCard";
import AccountBalanceWallet from "@mui/icons-material/AccountBalanceWallet";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import {
  authApi,
  useCreateStripePaymentIntentMutation,
  useInvestorInvestMutation,
} from "../../redux/api/authApi";

const TEAL = "#00bfa5";
const TEAL_DARK = "#0d9488";

const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

function parseInvestorSummaryData(queryResult) {
  const payload = queryResult?.data;
  return payload?.data?.data ?? payload?.data ?? payload;
}

/** Summary first; asset-by-id only when summary status is not funded. */
async function refreshInvestorAssetAfterInvest(dispatch, assetId) {
  if (!assetId) return;

  const summaryResult = await dispatch(
    authApi.endpoints.getInvestorAssetSummary.initiate(assetId, { forceRefetch: true })
  );

  const latestSummaryData = parseInvestorSummaryData(summaryResult);
  const latestStatusFunded =
    String(latestSummaryData?.asset?.status ?? "")
      .trim()
      .toLowerCase() === "funded";

  if (!latestStatusFunded) {
    await dispatch(
      authApi.endpoints.getInvestorAssetById.initiate(assetId, { forceRefetch: true })
    );
  }
}

/** Stripe Payment Element + pay button inside the nested payment dialog (after clientSecret is known). */
function StripePaymentPopupForm({
  paymentIntentIdFromApi,
  assetId,
  tokenAmount,
  paymentMethod,
  invest,
  onAfterInvest,
  onCloseInvestModal,
  onCloseStripePopup,
  setApiError,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setApiError("");
    setBusy(true);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (error) throw error;

      let paymentIntentId = paymentIntentIdFromApi;
      if (!paymentIntentId && paymentIntent?.id) {
        paymentIntentId = paymentIntent.id;
      }
      if (!paymentIntentId) {
        throw new Error("Missing payment reference.");
      }

      await invest({
        assetId,
        tokenAmount,
        paymentMethod,
        paymentIntentId,
      }).unwrap();

      await onAfterInvest?.();
      onCloseStripePopup();
      onCloseInvestModal();
    } catch (err) {
      setApiError(err?.data?.message || err?.message || err?.error || "Payment could not be completed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ mb: 2 }}>
        <PaymentElement />
      </Box>
      <Stack direction="row" spacing={1.5} justifyContent="flex-end">
        <Button
          type="button"
          variant="outlined"
          onClick={onCloseStripePopup}
          disabled={busy}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={busy || !stripe}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 2,
            bgcolor: TEAL,
            "&:hover": { bgcolor: TEAL_DARK },
          }}
        >
          {busy ? <CircularProgress size={24} color="inherit" /> : "Pay now"}
        </Button>
      </Stack>
    </Box>
  );
}

/** API payload values */
export const PAYMENT_METHODS = {
  SAR_BANK_TRANSFER: "SAR_BANK_TRANSFER",
  CREDIT_DEBIT_CARD: "CREDIT_DEBIT_CARD",
  CRYPTO_WALLET: "CRYPTO_WALLET",
};

function formatSar(n) {
  const x = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(x)) return "—";
  return new Intl.NumberFormat("en-SA", { maximumFractionDigits: 0 }).format(x);
}

function formatTokens(n) {
  const x = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(x)) return "0";
  return new Intl.NumberFormat("en-US").format(Math.round(x));
}

function InvestStepper({ activeStep }) {
  return (
    <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2, width: "100%", "& .MuiStepConnector-line": { borderTopWidth: 3 } }}>
      {["Token Amount", "Payment Method", "Confirmation"].map((label) => (
        <Step key={label}>
          <StepLabel
            sx={{
              "& .MuiStepLabel-label": { fontSize: "0.75rem", fontWeight: 600 },
              "& .MuiStepLabel-label.Mui-active": { fontWeight: 800, color: "#0f172a" },
              "& .MuiStepLabel-label.Mui-completed": { color: "#64748b" },
              "& .MuiStepIcon-root": { color: "#e2e8f0" },
              "& .MuiStepIcon-root.Mui-active": { color: TEAL },
              "& .MuiStepIcon-root.Mui-completed": { color: TEAL },
            }}
          >
            {label}
          </StepLabel>
        </Step>
      ))}
    </Stepper>
  );
}

export default function InvestorInvestModal({
  open,
  onClose,
  onSuccess,
  assetId,
  ownerId,
  assetName,
  pricePerTokenSar,
  minimumInvestmentSar,
  availableTokens,
  stripeAccountId,
}) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [tokenInput, setTokenInput] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS.CREDIT_DEBIT_CARD);
  const [apiError, setApiError] = useState("");
  const [stripePayOpen, setStripePayOpen] = useState(false);
  const [stripeSession, setStripeSession] = useState(null);
  const [openingStripe, setOpeningStripe] = useState(false);

  const [invest, { isLoading: isSubmitting }] = useInvestorInvestMutation();
  const [createStripeIntent] = useCreateStripePaymentIntentMutation();
  const shouldInitStripe = Boolean(
    stripePayOpen && stripeSession?.clientSecret && STRIPE_PUBLISHABLE_KEY
  );
  const stripePromise = useMemo(() => {
    if (!shouldInitStripe) return null;
    return loadStripe(STRIPE_PUBLISHABLE_KEY, {
      stripeAccount: stripeAccountId,
      developerTools: {
        assistant: {
          enabled: false,
        },
      },
    });
  }, [shouldInitStripe, stripeAccountId]);

  const price = Number(pricePerTokenSar);
  const minInv = Number(minimumInvestmentSar);
  const available = Math.max(0, Number(availableTokens) || 0);

  const minTokens = useMemo(() => {
    if (Number.isFinite(price) && price > 0 && Number.isFinite(minInv) && minInv > 0) {
      return Math.max(1, Math.ceil(minInv / price));
    }
    return 1;
  }, [price, minInv]);

  useEffect(() => {
    if (!open) {
      setStripePayOpen(false);
      setStripeSession(null);
      setOpeningStripe(false);
      return;
    }
    setStep(0);
    setTokenInput("");
    setPaymentMethod(PAYMENT_METHODS.CREDIT_DEBIT_CARD);
    setApiError("");
  }, [open]);

  const tokenAmount = useMemo(() => {
    const n = parseInt(String(tokenInput).replace(/\s/g, ""), 10);
    return Number.isFinite(n) ? n : NaN;
  }, [tokenInput]);

  const totalSar = useMemo(() => {
    if (!Number.isFinite(tokenAmount) || !Number.isFinite(price)) return 0;
    return tokenAmount * price;
  }, [tokenAmount, price]);

  const step1Error = useMemo(() => {
    if (available <= 0) return "No tokens available for purchase.";
    if (!Number.isFinite(tokenAmount) || tokenAmount < 1) return "Enter a valid number of tokens.";
    if (tokenAmount < minTokens) return `Minimum purchase is ${formatTokens(minTokens)} tokens (based on minimum investment).`;
    if (tokenAmount > available) return `Only ${formatTokens(available)} tokens available.`;
    if (!Number.isFinite(price) || price <= 0) return "Token price is not set for this asset.";
    return "";
  }, [tokenAmount, minTokens, available, price]);

  const handleContinueStep1 = () => {
    if (step1Error) return;
    setStep(1);
  };

  const handleAfterInvest = async () => {
    await refreshInvestorAssetAfterInvest(dispatch, assetId);
    await Promise.resolve(onSuccess?.());
  };

  const handleConfirm = async () => {
    if (paymentMethod === PAYMENT_METHODS.CREDIT_DEBIT_CARD) return;
    setApiError("");
    try {
      await invest({
        assetId,
        tokenAmount,
        paymentMethod,
      }).unwrap();
      await handleAfterInvest();
      onClose();
      navigate(`/investor/viewinvest/${assetId}?managed=1`, { state: { viewTab: "my" } });
    } catch (err) {
      setApiError(err?.data?.message || err?.error || "Payment could not be completed. Try again.");
    }
  };

  const handleCloseStripePopup = () => {
    setStripePayOpen(false);
    setStripeSession(null);
  };

  const handleOpenStripePopup = async () => {
    setApiError("");
    setOpeningStripe(true);
    try {
      const intentRes = await createStripeIntent({
        assetId,
        ownerId,
        tokenAmount: String(tokenAmount),
      }).unwrap();
      const resData = intentRes?.data ?? intentRes;
      const clientSecret = resData?.clientSecret;
      const paymentIntentId = resData?.paymentIntentId;
      if (!clientSecret) {
        throw new Error("Could not start card payment.");
      }
      setStripeSession({ clientSecret, paymentIntentId });
      setStripePayOpen(true);
    } catch (err) {
      setApiError(err?.data?.message || err?.message || err?.error || "Could not open payment. Try again.");
    } finally {
      setOpeningStripe(false);
    }
  };

  const paymentOptions = [
    // {
    //   value: PAYMENT_METHODS.SAR_BANK_TRANSFER,
    //   title: "SAR Bank Transfer",
    //   subtitle: "Pay via Saudi bank transfer",
    //   Icon: AccountBalance,
    //   iconBg: "#dcfce7",
    //   iconColor: "#166534",
    //   selectedBorder: "#cbd5e1",
    // },
    {
      value: PAYMENT_METHODS.CREDIT_DEBIT_CARD,
      title: "Credit/Debit Card",
      subtitle: "Pay with Visa or Mastercard",
      Icon: CreditCard,
      iconBg: "#ccfbf1",
      iconColor: TEAL_DARK,
      selectedBorder: TEAL,
    },
    {
      value: PAYMENT_METHODS.CRYPTO_WALLET,
      title: "Via Crypto Wallet",
      subtitle: "Pay with cryptocurrency (USDT, USDC, ETH)",
      Icon: AccountBalanceWallet,
      iconBg: "#7c3aed",
      iconColor: "#fff",
      selectedBorder: "#7c3aed",
    },
  ];

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflow: "visible" },
      }}
    >
      <Box sx={{ position: "relative", p: { xs: 2, sm: 3 }, pt: 4 }}>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", top: 8, right: 8, color: "#64748b" }}
          aria-label="Close"
        >
          <Close />
        </IconButton>

        <InvestStepper activeStep={step} />

        {apiError ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApiError("")}>
            {apiError}
          </Alert>
        ) : null}

        {step === 0 && (
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight={800} color="#0f172a">
              How many tokens would you like to buy?
            </Typography>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, borderColor: "#e2e8f0" }}>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                Number of Tokens
              </Typography>
              <TextField
                fullWidth
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value.replace(/[^\d]/g, ""))}
                placeholder="0"
                inputProps={{ inputMode: "numeric" }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
                <InfoOutlined sx={{ fontSize: 16, color: "#94a3b8" }} />
                <Typography variant="caption" color="text.secondary">
                  Minimum: {formatTokens(minTokens)} tokens · Available: {formatTokens(available)} tokens
                </Typography>
              </Stack>
              {step1Error ? (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                  {step1Error}
                </Typography>
              ) : null}
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography color="text.secondary">Total Investment</Typography>
                <Typography variant="h5" fontWeight={800} color="#0f172a">
                  SAR {formatSar(totalSar)}
                </Typography>
              </Stack>
            </Paper>
            <Button
              fullWidth
              variant="contained"
              endIcon={<ChevronRight />}
              onClick={handleContinueStep1}
              disabled={!!step1Error || !Number.isFinite(tokenAmount)}
              sx={{
                py: 1.25,
                textTransform: "none",
                fontWeight: 700,
                bgcolor: TEAL,
                borderRadius: 2,
                "&:hover": { bgcolor: TEAL_DARK },
              }}
            >
              Continue to Payment
            </Button>
          </Stack>
        )}

        {step === 1 && (
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight={800} color="#0f172a">
              Select Payment Method
            </Typography>
            <Stack spacing={1.5}>
              {paymentOptions.map((opt) => {
                const selected = paymentMethod === opt.value;
                const Icon = opt.Icon;
                return (
                  <Paper
                    key={opt.value}
                    variant="outlined"
                    onClick={() => setPaymentMethod(opt.value)}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      cursor: "pointer",
                      borderWidth: 2,
                      borderColor: selected ? opt.selectedBorder : "#e2e8f0",
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      transition: "border-color 0.2s",
                      "&:hover": { borderColor: selected ? opt.selectedBorder : "#cbd5e1" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: opt.iconBg,
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      <Icon sx={{ color: opt.iconColor, fontSize: 26 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={700}>{opt.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {opt.subtitle}
                      </Typography>
                    </Box>
                    {selected ? (
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          bgcolor: opt.value === PAYMENT_METHODS.CRYPTO_WALLET ? "#7c3aed" : TEAL,
                          display: "grid",
                          placeItems: "center",
                        }}
                      >
                        <Check sx={{ color: "#fff", fontSize: 18 }} />
                      </Box>
                    ) : null}
                  </Paper>
                );
              })}
            </Stack>
            <Stack direction="row" spacing={1.5} sx={{ pt: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setStep(0)}
                sx={{ textTransform: "none", fontWeight: 700, py: 1.25, borderRadius: 2 }}
              >
                Back
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={() => setStep(2)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  py: 1.25,
                  borderRadius: 2,
                  bgcolor: TEAL,
                  "&:hover": { bgcolor: TEAL_DARK },
                }}
              >
                Review Order
              </Button>
            </Stack>
          </Stack>
        )}

        {step === 2 && (
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight={800} color="#0f172a">
              Order Summary
            </Typography>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: "#fafafa", borderColor: "#e2e8f0" }}>
              {[
                { k: "Asset", v: assetName || "—" },
                { k: "Tokens", v: formatTokens(tokenAmount) },
                { k: "Price per Token", v: `SAR ${formatSar(price)}` },
                { k: "Fees", v: "SAR 0" },
              ].map((row) => (
                <Stack key={row.k} direction="row" justifyContent="space-between" sx={{ py: 1 }}>
                  <Typography color="text.secondary">{row.k}</Typography>
                  <Typography fontWeight={700}>{row.v}</Typography>
                </Stack>
              ))}
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography fontWeight={800}>Total</Typography>
                <Typography variant="h5" fontWeight={800}>
                  SAR {formatSar(totalSar)}
                </Typography>
              </Stack>
            </Paper>
            {paymentMethod === PAYMENT_METHODS.CREDIT_DEBIT_CARD ? (
              <Stack direction="row" spacing={1.5}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setStep(1)}
                  disabled={openingStripe}
                  sx={{ textTransform: "none", fontWeight: 700, py: 1.25, borderRadius: 2 }}
                >
                  Back
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleOpenStripePopup}
                  disabled={openingStripe}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    py: 1.25,
                    borderRadius: 2,
                    bgcolor: TEAL,
                    "&:hover": { bgcolor: TEAL_DARK },
                  }}
                >
                  {openingStripe ? <CircularProgress size={24} color="inherit" /> : "Confirm Payment"}
                </Button>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1.5}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setStep(1)}
                  disabled={isSubmitting}
                  sx={{ textTransform: "none", fontWeight: 700, py: 1.25, borderRadius: 2 }}
                >
                  Back
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleConfirm}
                  disabled={isSubmitting}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    py: 1.25,
                    borderRadius: 2,
                    bgcolor: TEAL,
                    "&:hover": { bgcolor: TEAL_DARK },
                  }}
                >
                  {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Confirm Payment"}
                </Button>
              </Stack>
            )}
          </Stack>
        )}
      </Box>
    </Dialog>

    <Dialog
      open={stripePayOpen && Boolean(stripeSession?.clientSecret)}
      onClose={handleCloseStripePopup}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
    >
      <Box sx={{ position: "relative", p: { xs: 2, sm: 3 }, pt: 4 }}>
        <IconButton
          onClick={handleCloseStripePopup}
          sx={{ position: "absolute", top: 8, right: 8, color: "#64748b" }}
          aria-label="Close payment"
        >
          <Close />
        </IconButton>
        <Typography variant="h6" fontWeight={800} color="#0f172a" sx={{ mb: 0.5 }}>
          Secure payment
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter your card details in the secure Stripe form below.
        </Typography>
        {stripeSession?.clientSecret && stripePromise ? (
          <Elements
            key={stripeSession.clientSecret}
            stripe={stripePromise}
            options={{
              clientSecret: stripeSession.clientSecret,
              appearance: { theme: "stripe" },
            }}
          >
            <StripePaymentPopupForm
              paymentIntentIdFromApi={stripeSession.paymentIntentId}
              assetId={assetId}
              tokenAmount={tokenAmount}
              paymentMethod={paymentMethod}
              invest={invest}
              onAfterInvest={handleAfterInvest}
              onCloseInvestModal={onClose}
              onCloseStripePopup={handleCloseStripePopup}
              setApiError={setApiError}
            />
          </Elements>
        ) : null}
      </Box>
    </Dialog>
    </>
  );
}
